import { useState, useEffect, useRef } from "react";
import styles from "./Register.module.css";
import { useAuthErrorMessage, webAuth } from "../utils/userJourneyHelper";
import { useNavigate } from "react-router-dom";
import { useRecaptcha } from "../hooks/useRecaptcha";

function Register() {
  const {
    verifyHuman,
    challengeVisible,
    challengeError,
    challengeRef,
  } = useRecaptcha("signup");
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    company: "",
    businessEmail: "",
    phoneNumber: "",
    city: "",
    password: "",
    confirmPassword: "",
    termsAccepted: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useAuthErrorMessage();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError();
    setLoading(true);
    if (formData.password !== formData.confirmPassword) {
      setError("Password and Confirm Password fields don't match.");
      setLoading(false);
      return;
    }

    const { ok, token, error: verifyError } = await verifyHuman();

    if (!ok) {
      setError(verifyError);
      setLoading(false);
      return;
    }

    setLoading(true);

    webAuth.signup(
      {
        connection: "Username-Password-Authentication",
        email: formData.businessEmail,
        password: formData.password,
        user_metadata: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          company: formData.company,
          city: formData.city,
          phoneNumber: formData.phoneNumber,
        },
        recaptcha_token: token,
      },
      (response) => {
        if (response == null) {
          navigate(
            "/Auth/Login?success_description=Thank you for registering for the Irish Funds Member Portal. Please check your email and follow the instructions to complete the registration process."
          );
          return null;
        }
        const body = response?.original?.response?.body;
        const errorMessage =
          body?.friendly_message ||
          body?.message ||
          body?.description ||
          body?.error ||
          response?.description ||
          response?.error ||
          "Registration failed. Please try again, or contact us if the problem persists.";
        setError(errorMessage);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    if (challengeVisible) {
      setLoading(false);
    }
  }, [challengeVisible]);

  const el = useRef(null);

  useEffect(() => {
    if (error && el.current) {
      el.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [error]);

  return (
    <div className={styles.auth}>
      <span className={styles.heroImageWrapper}>
        <span className={styles.heroImageSizer}>
          <img
            alt=""
            aria-hidden="true"
            src="data:image/svg+xml,%3csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20version=%271.1%27%20width=%271440%27%20height=%27899%27/%3e"
            className={styles.heroImageSpacer}
          />
        </span>
        <img
          alt=""
          srcSet="https://cdn.irishfunds.ie/3871x2582/96df9d4546/irish-funds-conference-2024-083.jpg/m/1440x0/filters:quality(80)?_w=1440 1x, https://cdn.irishfunds.ie/3871x2582/96df9d4546/irish-funds-conference-2024-083.jpg/m/1440x0/filters:quality(80)?_w=1440 2x"
          src="https://cdn.irishfunds.ie/3871x2582/96df9d4546/irish-funds-conference-2024-083.jpg/m/1440x0/filters:quality(80)?_w=1440"
          decoding="async"
          className={styles.heroImage}
        />
      </span>
      <section
        className={`${styles.auth_section} ${styles.register} ${
          loading && styles.loading
        }`}
      >
        <div className={styles.header}>
          <svg
            role="presentation"
            viewBox="0 0 644 228"
            preserveAspectRatio="none"
            className={styles.shape}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M7.01758 -290.458L1006.94 473.78L1124.9 210.611L7.01758 -290.458Z"
              fill="#009247"
            ></path>
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M1125.6 209.059L-110.246 -28.8413L7.71373 -292.01L1125.6 209.059Z"
              fill="#3FAE48"
            ></path>
          </svg>
          <div className={styles.logo}>
            <span className={styles.logoWrapper}>
              <span className={styles.logoSizer}>
                <img
                  alt=""
                  aria-hidden="true"
                  src="data:image/svg+xml,%3csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20version=%271.1%27%20width=%2772%27%20height=%2734%27/%3e"
                  className={styles.logoSpacer}
                />
              </span>
              <img
                alt="Irish Funds Logo"
                srcSet="/assets/img/IFlogo-white-small.png?_w=72 1x, /assets/img/IFlogo-white-small.png?_w=72 2x"
                src="/assets/img/IFlogo-white-small.png?_w=72"
                decoding="async"
                className={styles.logoImage}
              />
            </span>
          </div>
        </div>
        <form className={styles.registerForm} onSubmit={handleSubmit} ref={el}>
          <span className={styles.welcome}>
            This portal is open to members only
          </span>
          <h1 className={styles.heading}>Enter your details below</h1>
          {error && <p className={styles.error}>Error: {error}</p>}
          <div className={styles.fields}>
            <div className={styles.fields2}>
              <div>
                <label htmlFor="firstName">First Name</label>
                <input
                  id="firstName"
                  type="text"
                  name="firstName"
                  required
                  aria-required="true"
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label htmlFor="lastName">Last Name</label>
                <input
                  id="lastName"
                  type="text"
                  name="lastName"
                  required
                  aria-required="true"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
            </div>
            <label htmlFor="company">Company</label>
            <input
              id="company"
              type="text"
              name="company"
              required
              aria-required="true"
              value={formData.company}
              onChange={handleChange}
            />
            <label htmlFor="businessEmail">Business Email</label>
            <input
              id="businessEmail"
              type="email"
              name="businessEmail"
              required
              aria-required="true"
              value={formData.businessEmail}
              onChange={handleChange}
            />
            <label htmlFor="phoneNumber">Phone Number - Optional</label>
            <input
              id="phoneNumber"
              type="phone"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
            />
            <label htmlFor="city">City</label>
            <input
              id="city"
              type="text"
              name="city"
              required
              aria-required="true"
              value={formData.city}
              onChange={handleChange}
            />
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
            />
            <p className={styles.helptext}>
              Passwords must be 10 characters long and contain at least one
              uppercase, one lowercase, and one numeric character.
            </p>
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
            <h2 className={styles.subheading}>Terms &amp; Conditions</h2>
            <div className={styles.termsWrapper}>
              <p>Member Portal and AI Assistant (Daire) Terms & Conditions</p>
              <p>
                The Irish Funds Member Portal ("the Portal") is a secure,
                members-only platform designed to provide access to resources,
                information, and updates relevant to the Irish funds industry.
                Access to and use of the Portal is strictly limited to staff of
                Irish Funds member firms.
              </p>
              <p>
                Please read the following terms carefully. By using the Portal,
                you acknowledge and agree to the terms outlined below:
              </p>
              <p>1. Access and Confidentiality</p>
              <ul>
                <li>
                  All materials provided through the Portal are intended solely
                  for the internal use of member firms.
                </li>
                <li>
                  Content is private, confidential, and must not be shared
                  externally without prior authorisation from Irish Funds.
                </li>
                <li>
                  Members are responsible for maintaining the confidentiality of
                  any information accessed through the Portal.
                </li>
                <li>
                  Sharing Working Group member details outside the Irish Funds
                  member network is prohibited without prior authorisation from
                  Irish Funds.
                </li>
              </ul>
              <p>2. Nature of Information</p>
              <ul>
                <li>
                  The Portal provides general information for reference purposes
                  only.
                </li>
                <li>
                  Content is provided "as is" and does not constitute legal,
                  tax, investment, accounting, financial, or professional
                  advice.
                </li>
                <li>
                  Users should seek qualified professional advice before acting
                  on any information obtained from the Portal.
                </li>
              </ul>
              <p>3. Accuracy and Updates</p>
              <ul>
                <li>
                  While Irish Funds aims to provide accurate and up-to-date
                  content, no guarantees are made regarding completeness,
                  reliability, or timeliness.
                </li>
                <li>
                  Access to the Portal may be temporarily unavailable due to
                  maintenance or technical issues.
                </li>
                <li>
                  Irish Funds is not liable for any loss or damage resulting
                  from reliance on information contained within the Portal.
                </li>
              </ul>
              <p>4. Member Contributions</p>
              <ul>
                <li>
                  Any content or material uploaded by a member is provided "as
                  is," and Irish Funds makes no warranties regarding its
                  accuracy, security, or suitability.
                </li>
                <li>
                  Members remain responsible for ensuring that any contributions
                  do not infringe third-party rights or breach any applicable
                  laws.
                </li>
              </ul>
              <p>5. Monitoring and Content Management</p>
              <ul>
                <li>
                  Irish Funds reserves the right to monitor, edit, or remove
                  content on the Portal at its discretion.
                </li>
                <li>
                  While there is no obligation to screen all material, a "notice
                  and takedown" approach is in place.
                </li>
                <li>
                  If you believe any content infringes rights or is otherwise
                  inappropriate, please notify Irish Funds immediately via
                  memberportal@irishfunds.ie.
                </li>
              </ul>
              <p>6. Limitation of Liability</p>
              <ul>
                <li>Use of the Portal is at your own risk.</li>
                <li>
                  To the fullest extent permitted by law, Irish Funds and its
                  member firms accept no responsibility for any direct,
                  indirect, or consequential loss arising from your use of the
                  Portal or reliance on its content.
                </li>
              </ul>
              <p>7. Legal Framework</p>
              <ul>
                <li>
                  These Terms & Conditions are governed by the laws of Ireland.
                </li>
                <li>
                  Irish Funds may update these terms from time to time.
                  Continued use of the Portal signifies your acceptance of the
                  most recent version.
                </li>
              </ul>
              <p>
                AI Assistant (ChatGPT) - By using this service, you acknowledge
                and consent to the following:
              </p>
              <ol>
                <li>
                  Use of Data: Your interactions with the AI Assistant may be
                  monitored and stored by Irish Funds for quality assurance,
                  service improvement, and trend analysis. This data is not
                  used to train the AI model, and no personal data is shared
                  with OpenAI or external third parties.
                </li>
                <li>
                  Nature of Information Provided: The AI Assistant provides
                  general, non-binding information based on curated datasets.
                  It does not constitute legal, tax, accounting, or financial
                  advice. Daire will prioritise Irish Funds curated
                  information when responding but may provide information from
                  its existing corpus of knowledge.
                </li>
                <li>
                  Responsibility: You remain responsible for verifying any
                  information provided and for taking appropriate professional
                  advice where necessary. The system will provide you with a
                  list of information sources that were used in the generation
                  of the response.
                </li>
                <li>
                  Privacy and Security: All interactions are subject to the
                  Irish Funds Privacy Policy. Please review our full{" "}
                  <a href="https://www.irishfunds.ie/privacy/">
                    Privacy Policy
                  </a>
                  .
                </li>
                <li>
                  Support and Reporting Issues: For concerns or to report
                  inaccurate responses, contact{" "}
                  <a href="mailto:memberportal@irishfunds.ie">
                    memberportal@irishfunds.ie
                  </a>
                  .
                </li>
              </ol>
            </div>
            <div className={`${styles.agreement} ${styles.terms} `}>
              <div>
                <input
                  type="checkbox"
                  id="DecisionBox"
                  className={`${styles.checkbox} ${styles.visuallyHidden}`}
                  name="terms"
                  required
                  value={formData.termsAccepted}
                  onChange={handleChange}
                />
                <label htmlFor="DecisionBox">
                  I have read and accept the terms and conditions outlined
                  above.
                </label>
              </div>
            </div>
            <div className={styles.recaptcha} hidden={!challengeVisible}>
                <p className={styles.recaptchaHint}>
                  Please confirm you are not a robot to continue.
                </p>
                {challengeError && <p className={styles.error}>{challengeError}</p>}
                <div ref={challengeRef} />
              </div>
            <button
              type="submit"
              disabled={loading || challengeVisible}
              className={`${styles.registerButton} ${styles.registerButton_secondary}`}
            >
              Register
            </button>
            <p className={styles.back}>
              <a href="/Auth/Login/">Back to login</a>
            </p>
          </div>
        </form>
      </section>
    </div>
  );
}

export default Register;
